import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { type ColumnDef } from "@tanstack/react-table";
import { IconChevronRight, IconFolder } from "@tabler/icons-react";

import { BrandLogo } from "@/components/brand/BrandLogo";
import { DataTable } from "@/components/ui/data-table";
import { EmptyState } from "@/components/ui/empty-state";
import { DashboardCard } from "@/components/dashboard/dashboard-card";
import { listAffiliateEligiblePrograms, type EligibleContentProgramRow } from "@/services/contentProgram.service";
import { formatNumber } from "@/utils/formatters";

export function brandLabel(brand: EligibleContentProgramRow["brandDetail"]): string {
  return brand.companyName || `${brand.firstName} ${brand.lastName}`.trim() || "Brand";
}

const shortDate = (value: string) =>
  new Date(value).toLocaleDateString("en-US", { month: "short", day: "numeric" });

function mediaLabel(row: EligibleContentProgramRow) {
  const types = row.contentConfig?.allowedMediaTypes ?? [];
  const image = types.includes("IMAGE");
  const video = types.includes("VIDEO");
  return image && video ? "Images and videos" : video ? "Videos" : image ? "Images" : "Any";
}

/** Programs the affiliate can submit to, one row each; the row opens the submit form. */
export default function AffiliateContentProgramsList() {
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({
    queryKey: ["affiliate-eligible-content-programs"],
    queryFn: listAffiliateEligiblePrograms,
  });

  const rows = useMemo(
    () =>
      [...(data ?? [])].sort(
        (a, b) => brandLabel(a.brandDetail).localeCompare(brandLabel(b.brandDetail)) || a.title.localeCompare(b.title)
      ),
    [data]
  );

  const columns = useMemo<ColumnDef<EligibleContentProgramRow>[]>(
    () => [
      {
        id: "title",
        header: "Program",
        cell: ({ row }) => (
          <span className="flex min-w-0 items-center gap-2.5">
            <BrandLogo
              src={row.original.brandDetail.brandProfileImage}
              name={brandLabel(row.original.brandDetail)}
              className="size-7 shrink-0 rounded-full"
              initialsClassName="text-[10px]"
            />
            <span className="min-w-0">
              <span className="block max-w-[18rem] truncate font-medium text-foreground" title={row.original.title}>
                {row.original.title}
              </span>
              <span className="block truncate text-xs text-muted-foreground">{brandLabel(row.original.brandDetail)}</span>
            </span>
          </span>
        ),
      },
      {
        id: "commission",
        header: "Commission",
        meta: { fit: true },
        cell: ({ row }) => <span className="tabular-nums text-foreground">{row.original.commissionRate}%</span>,
      },
      {
        id: "media",
        header: "Media",
        meta: { fit: true },
        cell: ({ row }) => <span className="whitespace-nowrap text-foreground">{mediaLabel(row.original)}</span>,
      },
      {
        id: "placements",
        header: "Placements",
        meta: { fit: true },
        cell: ({ row }) => (
          <span className="whitespace-nowrap tabular-nums text-foreground">
            {row.original.contentConfig?.allowedAspectRatios?.join(", ") || "Any"}
          </span>
        ),
      },
      {
        id: "products",
        header: "Products",
        meta: { fit: true },
        cell: ({ row }) => (
          <span className="whitespace-nowrap tabular-nums text-foreground">
            {row.original.entitledProductIds?.length ? formatNumber(row.original.entitledProductIds.length) : "All"}
          </span>
        ),
      },
      {
        id: "created",
        header: "Opened",
        meta: { fit: true },
        cell: ({ row }) => (
          <span className="whitespace-nowrap text-muted-foreground">{shortDate(row.original.createdAt)}</span>
        ),
      },
      {
        id: "open",
        header: "",
        meta: { fit: true, cellClassName: "text-right", skeleton: <span /> },
        cell: () => (
          <span className="inline-flex items-center gap-2">
            <span className="text-xs font-medium text-muted-foreground">Submit content</span>
            <IconChevronRight size={16} stroke={1.75} className="text-muted-foreground" aria-hidden />
          </span>
        ),
      },
    ],
    []
  );

  return (
    <DashboardCard title="Programs" bodyClassName="px-3 pb-3">
      {!isLoading && rows.length === 0 ? (
        <EmptyState
          bordered={false}
          icon={<IconFolder stroke={1.75} />}
          title="No content programs yet"
          body="Brands you are enrolled with open programs here when they want creative"
        />
      ) : (
        <DataTable
          columns={columns}
          data={rows}
          isLoading={isLoading}
          skeletonRows={4}
          getRowId={(row) => row.id}
          onRowClick={(row) => navigate(`/affiliate/content-programs/${row.id}/submit`)}
          emptyText="No content programs yet"
        />
      )}
    </DashboardCard>
  );
}
